import React, { createContext, useContext, useState, useEffect } from "react";
import { redirect } from "react-router-dom";

interface AuthContextType {
  isAuthenticated: boolean;
  userRole: string | null;
  setIsAuthenticated: (authStatus: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (error) {
    console.error("Fehler beim Parsen des Tokens:", error);
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuthStatus = () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      const decodedToken = parseJwt(token);
      if (decodedToken && decodedToken.exp > Date.now() / 1000) {
        setIsAuthenticated(true);
        setUserRole(decodedToken.role || null);
      } else {
        localStorage.removeItem("authToken");
        setIsAuthenticated(false);
        setUserRole(null);
        redirect("/login");
      }
    } else {
      setIsAuthenticated(false);
      setUserRole(null);
      redirect("/login");
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuthStatus(); // Initialer Auth-Check
    window.addEventListener("storage", checkAuthStatus); // Echtzeitüberwachung auf Token-Änderung
    return () => window.removeEventListener("storage", checkAuthStatus);
  }, []);

  if (loading) {
    return <div>Lädt...</div>;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, userRole, setIsAuthenticated }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth muss innerhalb eines AuthProvider verwendet werden");
  }
  return context;
};
